import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react'
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser'
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react'

const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
}

export const msalInstance = new PublicClientApplication(msalConfig)

const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
}

const rawApiScope = process.env.REACT_APP_AZURE_API_SCOPE || ''
const clientId = process.env.REACT_APP_AZURE_CLIENT_ID || ''

const normalizeApiScope = (scope, appClientId) => {
  const cleanedScope = String(scope || '')
    .trim()
    .replace(/^['\"]|['\"]$/g, '')
  if (!appClientId) return cleanedScope

  const normalizedClientId = appClientId.toLowerCase()
  const lowerScope = cleanedScope.toLowerCase()
  const sameAppPrefix = `api://${normalizedClientId}/`

  // AADSTS90009: if app requests token for itself, use GUID-format scope (<client-id>/.default)
  if (
    lowerScope === `api://${normalizedClientId}` ||
    lowerScope.startsWith(sameAppPrefix) ||
    lowerScope === `${normalizedClientId}/.default`
  ) {
    return `${appClientId}/.default`
  }

  return cleanedScope || `${appClientId}/.default`
}

const apiScope = normalizeApiScope(rawApiScope, clientId)

const apiTokenRequest = {
  scopes: [apiScope].filter(Boolean),
}

const TOKEN_STORAGE_KEY = 'vda_auth_token'

const AuthContext = createContext(null)

const AuthContextProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [token, setToken] = useState('')

  const activeAccount = accounts[0] || null

  useEffect(() => {
    if (isAuthenticated && activeAccount) {
      instance
        .acquireTokenSilent({ ...apiTokenRequest, account: activeAccount })
        .then(response => {
          setToken(response.accessToken)
          sessionStorage.setItem(TOKEN_STORAGE_KEY, response.accessToken)
        })
        .catch(err => {
          console.error('Failed to acquire API token', {
            scope: apiScope,
            message: err?.message,
            errorCode: err?.errorCode,
            subError: err?.subError,
          })
          if (err instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect({
              ...apiTokenRequest,
              account: activeAccount,
            })
          }
        })
    } else {
      setToken('')
      sessionStorage.removeItem(TOKEN_STORAGE_KEY)
    }
  }, [isAuthenticated, activeAccount?.localAccountId])

  const auth = useMemo(
    () => ({
      loggedIn: isAuthenticated,
      isLoading: inProgress !== InteractionStatus.None,
      token,
      login: () => instance.loginRedirect(loginRequest),
      logout: redirectUri =>
        instance.logoutRedirect({ postLogoutRedirectUri: redirectUri }),
      getUserInfo: async () => {
        if (activeAccount) {
          return {
            email: activeAccount.username,
            name: activeAccount.name,
          }
        }
        return {}
      },
    }),
    [isAuthenticated, inProgress, token, instance, activeAccount]
  )

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

export const AuthProviderReact = ({ children }) => (
  <MsalProvider instance={msalInstance}>
    <AuthContextProvider>{children}</AuthContextProvider>
  </MsalProvider>
)

export const withAuth = Component => {
  const WithAuthComponent = props => {
    const auth = useContext(AuthContext)
    return <Component {...props} auth={auth} />
  }
  WithAuthComponent.displayName = `withAuth(${
    Component.displayName || Component.name
  })`
  return WithAuthComponent
}

export const getToken = () => sessionStorage.getItem(TOKEN_STORAGE_KEY)
